import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { SelectBox } from 'devextreme-react';
import { NuvoTooltip } from '@nuvolo/nuux-core/components/NuvoTooltip';
import { useNavigate } from 'react-router-dom';
import type { ISelectBoxOptions } from 'devextreme-react/select-box';
import type { ValueChangedInfo } from 'devextreme/ui/editor/editor';
import useDataStore, { DataStoreOptions } from '#/common/hooks/useDataStore';
import parseUrl from '#/common/utils/parsers/parseUrl';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import type { EditorProps } from './EditorProps';
import { border, borderRadius } from './common';
import If from '../../If';
import Validator, { RequiredRule } from 'devextreme-react/validator';
import { createScrollCloseDropdownOptions } from '../utils/dropdownScrollClose';
import { getItemOptions } from '../formItemOption';
import TMMButton from '../../TMMButton';
import SelectCard from '#/common/components/SelectCard';
import dotWalk from '#/common/utils/dotWalk';

interface RelatedItemsOptions extends ISelectBoxOptions {
  valueExpr?: string;
  validationRules?: any[];
  button?: {
    hide?: boolean;
    tooltip?: string;
    url?: string;
    icon?: string;
    desktopOnly?: boolean;
    readOnly?: boolean;
  };
  dataStoreOptions?: Pick<
    DataStoreOptions<unknown>,
    'getItemList' | 'getItem' | 'filter' | 'accessTo'
  >;
  subtitleExpr?: string;
  customItemRender?: (
    data: Record<string, unknown>,
    valueExpr: string,
    displayExpr?: string | ((item: any) => string),
  ) => React.ReactNode;
  dropdownClassName?: string;
  dynamic?: {
    watch: string[];
    build: (ctx: { record: Record<string, any>; value: any }) => any | null;
    onWatchChange?: (args: {
      changed: { field: string; value: any; prev?: any }[];
      record: Record<string, any>;
      value: any;
      form: any;
    }) => void;
  };
}

export interface RelatedItemsProps {
  editorType: 'RelatedItems';
  editorOptions: RelatedItemsOptions;
}

export type { RelatedItemsOptions };

const RelatedItems: React.FC<EditorProps<RelatedItemsOptions>> = ({
  dataField,
  isRequired,
  value,
  editorOptions,
  form,
}) => {
  const {
    button,
    dataSource,
    displayExpr,
    valueExpr = 'id',
    dataStoreOptions,
    searchEnabled = true,
    dynamic,
    subtitleExpr,
    customItemRender,
    dropdownClassName,
    value: initValue,
    ...selectProps
  } = editorOptions;

  const readonly = form.option('readOnly') || editorOptions.readOnly;
  const isItemRequired = isRequired || getItemOptions(form, dataField).isRequired || undefined;

  const {
    hide = false,
    tooltip = '',
    url = '',
    icon = 'arrow-right',
    desktopOnly = false,
    readOnly: isBtnReadOnly = false,
  } = button ?? {};

  const [watchTick, setWatchTick] = useState(0); // re-render trigger for parent changes
  const record = useMemo(() => {
    return (form.option('formData') as Record<string, unknown>) || {};
  }, [form, watchTick]);
  const selectedValue = record[dataField];
  const tooltipMsg = parseUrl(tooltip, { value: selectedValue });

  const navigate = useNavigate();

  // Listen for field changes → notify + bump tick
  useEffect(() => {
    if (!dynamic?.watch?.length || !form) return;

    const handler = (e: { dataField?: string; value?: any; previousValue?: any }) => {
      const field = e?.dataField;
      if (!field || !dynamic.watch.includes(field)) return;

      dynamic.onWatchChange?.({
        changed: [{ field, value: e.value, prev: e.previousValue }],
        record: form.option('formData') as Record<string, any>,
        value: selectedValue,
        form,
      });

      setWatchTick((t) => t + 1); // force recompute of record/filters
    };

    (form as any).on?.('fieldDataChanged', handler);
    return () => (form as any).off?.('fieldDataChanged', handler);
  }, [dynamic?.watch, dynamic?.onWatchChange, form, selectedValue]);

  // Build filters: tenant + base + dynamic
  const tenantId = record?.tenantId ?? null;
  const tenantFilter = tenantId ? ['tenantId', '=', tenantId] : '';
  const baseFilter = dataStoreOptions?.filter ?? null;

  const dynamicFilter = useMemo(() => {
    if (!dynamic?.build) return null;
    try {
      return dynamic.build({ record: record as any, value: selectedValue });
    } catch {
      return null;
    }
  }, [dynamic?.build, record, selectedValue, watchTick]);

  const mergedFilter = useMemo(() => {
    const parts = [baseFilter, tenantFilter, dynamicFilter].filter(Boolean);
    if (!parts.length) return null;
    if (parts.length === 1) return parts[0];

    return parts.flatMap((part, i) => (i === 0 ? [part] : ['AND', part]));
  }, [baseFilter, tenantFilter, dynamicFilter]);

  // A key that changes when parents or mergedFilter change
  const dynamicKey = useMemo(() => {
    const watchVals = (dynamic?.watch ?? []).map((f) => (record as any)?.[f] ?? null);
    return JSON.stringify({ watchVals, mergedFilter });
  }, [dynamic?.watch, record, mergedFilter]);

  const queryDataStore = useDataStore<Record<string, unknown>>({
    ...dataStoreOptions,
    filter: mergedFilter,
    cacheEnabled: true,
    key: valueExpr,
  });

  const ds = dataSource || queryDataStore;

  const onClick = useCallback(() => {
    const urlWithValue = parseUrl(url, { value: selectedValue });
    navigate(urlWithValue);
  }, [navigate, selectedValue, url]);

  const onValueChanged = useCallback(
    (e: ValueChangedInfo) => {
      form.updateData({
        [dataField]: e.value,
      });
    },
    [dataField, form],
  );

  const { isDesktop } = useViewport();
  const showButton = !hide && (!desktopOnly || isDesktop);

  // Custom item render using SelectCard
  const itemRender = useCallback(
    (data: Record<string, unknown>) => {
      if (!data) return null;

      // Use custom renderer if provided, otherwise use default
      if (customItemRender) {
        return customItemRender(data, valueExpr, displayExpr);
      }

      const id = String(data[valueExpr] || '');
      const title = String(
        displayExpr
          ? typeof displayExpr === 'string'
            ? dotWalk(data, displayExpr) || 'Unnamed'
            : displayExpr(data) || 'Unnamed'
          : 'Unnamed',
      );
      const subtitle = subtitleExpr ? String(dotWalk(data, subtitleExpr) || '') : undefined;

      return <SelectCard id={id} title={title} subtitle={subtitle} />;
    },
    [valueExpr, displayExpr, subtitleExpr, customItemRender],
  );

  // Dropdown options with card styling
  const dropdownOptions = useMemo(() => {
    const baseOptions = createScrollCloseDropdownOptions();
    const columnClass = dropdownClassName || 'cards-single-column';

    return {
      ...baseOptions,
      minHeight: 300,
      maxHeight: 300,
      wrapperAttr: {
        ...baseOptions.wrapperAttr,
        class: `${baseOptions.wrapperAttr?.class || ''} cards-dropdown-popup ${columnClass}`.trim(),
      },
    };
  }, [dropdownClassName]);

  return (
    <NuvoBox display="flex" flexDirection="row" width="100%">
      <If condition={showButton}>
        <NuvoBox
          display="flex"
          alignItems="center"
          border={border}
          borderRight="0px"
          borderRadius={`${borderRadius} 0 0 ${borderRadius}`}>
          <NuvoTooltip enabled={!!tooltip} content={tooltipMsg}>
            <TMMButton icon={icon} onClick={onClick} disabled={!selectedValue || isBtnReadOnly} />
          </NuvoTooltip>
        </NuvoBox>
      </If>
      <NuvoBox flex="1" style={{ overflow: 'visible' }}>
        <SelectBox
          key={dynamicKey}
          displayExpr={displayExpr}
          valueExpr={valueExpr}
          dataSource={ds}
          readOnly={readonly}
          searchEnabled={searchEnabled}
          searchMode="contains"
          searchTimeout={500}
          minSearchLength={0}
          showClearButton={true}
          placeholder={selectProps.placeholder || 'Search and select...'}
          acceptCustomValue={false}
          defaultValue={selectedValue || initValue}
          dropDownOptions={dropdownOptions}
          itemRender={itemRender}
          // value={selectedValue} // ! Do not set value, use defaultValue instead
          {...selectProps}
          onValueChanged={onValueChanged}>
          {isItemRequired && (
            <Validator validationGroup="FormValidationGroup">
              <RequiredRule key={dataField} message={`"${selectProps.label}" is required`} />
            </Validator>
          )}
        </SelectBox>
      </NuvoBox>
    </NuvoBox>
  );
};

export default React.memo(RelatedItems);
